import { api } from '@packages/backend/convex/_generated/api';
import {
  PUSH_NOTIFICATION_STATUSES,
  type PushNotificationCvx,
  type PushNotificationStatus,
  zPushNotificationStatus,
} from '@packages/shared';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from 'convex-helpers/react';
import { formatDistanceToNow } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';

const LIMIT = 100;

export const Route = createFileRoute('/a/push-notifications/')({
  validateSearch: (search) => ({
    status: zPushNotificationStatus.default('pending').parse(search.status),
  }),
  component: Component,
});

function Component() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const {
    data: rawNotifications,
    error,
    isError,
    isPending,
  } = useQuery(api.pushNotifications.listByStatus, {
    status: search.status,
    limit: LIMIT,
  });

  const formatDate = useCallback((timestamp: number | null) => {
    if (!timestamp) return '-';
    return formatDistanceToNow(timestamp, { addSuffix: true });
  }, []);

  const getStatusTimestamp = useCallback(
    (notification: PushNotificationCvx) => {
      switch (search.status) {
        case 'accepted':
          return notification.acceptedAt;
        case 'sent':
          return notification.sentAt;
        case 'error':
          return notification.errorAt;
        default:
          return notification._creationTime;
      }
    },
    [search.status],
  );

  const getStatusTimestampLabel = useCallback((status: PushNotificationStatus) => {
    switch (status) {
      case 'accepted':
        return 'Accepted';
      case 'sent':
        return 'Sent';
      case 'error':
        return 'Error';
      default:
        return 'Created';
    }
  }, []);

  // Sort notifications on the client side based on the appropriate timestamp for the current status
  const notifications = useMemo(() => {
    if (!rawNotifications) return [];

    return [...rawNotifications].sort((a, b) => {
      const timestampA = getStatusTimestamp(a) || 0;
      const timestampB = getStatusTimestamp(b) || 0;

      // Sort in descending order (newest first)
      return timestampB - timestampA;
    });
  }, [rawNotifications, getStatusTimestamp]);

  if (isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Push Notifications</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-red-500">Error loading notifications. {error.message}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Push Notifications</CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs
          value={search.status}
          onValueChange={(value) => {
            navigate({
              from: Route.fullPath,
              search: { status: zPushNotificationStatus.parse(value) },
              replace: true,
            });
          }}
        >
          <TabsList>
            {PUSH_NOTIFICATION_STATUSES.map((status) => (
              <TabsTrigger key={status} value={status}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </TabsTrigger>
            ))}
          </TabsList>
          {isPending ? (
            <LoadingIcon className="mt-4" />
          ) : (
            <TabsContent value={search.status}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{getStatusTimestampLabel(search.status)}</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Body</TableHead>
                    {search.status === 'error' && <TableHead>Error</TableHead>}
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {notifications.map((notification) => (
                    <TableRow key={notification._id}>
                      <TableCell>{formatDate(getStatusTimestamp(notification))}</TableCell>
                      <TableCell>{notification.expoPushNotification.title || '-'}</TableCell>
                      <TableCell>{notification.expoPushNotification.body || '-'}</TableCell>
                      {search.status === 'error' && (
                        <TableCell>
                          {notification.error || '-'}
                          {notification.errorMessage && (
                            <div
                              className="text-sm text-gray-500 truncate max-w-[250px]"
                              title={notification.errorMessage}
                            >
                              {notification.errorMessage}
                            </div>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <Link
                          to="/a/push-notifications/$notificationId"
                          params={{ notificationId: notification._id }}
                          search={{ status: search.status }}
                          className="text-blue-600 hover:text-blue-800 underline"
                        >
                          View
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                  {notifications.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-4">
                        No notifications found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TabsContent>
          )}
        </Tabs>
      </CardContent>
    </Card>
  );
}
